import { createContext, useContext, useState, useEffect } from 'react';

const THEMES = {
  professional: {
    id: 'professional',
    name: 'Professional',
    icon: '💼',
    colors: {
      primary: '#0066cc',
      background: '#f5f5f7',
      darkBackground: '#1c1c1e',
      text: '#1d1d1f',
      buttonBg: '#2c2c2e'
    },
    layout: 'grid'
  },
  casual: {
    id: 'casual',
    name: 'Casual',
    icon: '😊',
    colors: {
      primary: '#FF6B6B',
      background: '#FFF5F5',
      darkBackground: '#2D1B1B',
      text: '#2D1B1B',
      buttonBg: '#FF6B6B'
    },
    layout: 'grid',
    showReactions: true
  },
  gaming: {
    id: 'gaming',
    name: 'Gaming',
    icon: '🎮',
    colors: {
      primary: '#00FF88',
      background: '#0A0E27',
      darkBackground: '#000000',
      text: '#00FF88',
      buttonBg: '#1a1f3a',
      neon: true
    },
    layout: 'grid'
  },
  focus: {
    id: 'focus',
    name: 'Focus Mode',
    icon: '🎯',
    colors: {
      primary: '#8B5CF6',
      background: '#FAF5FF',
      darkBackground: '#1F1B24',
      text: '#2E1F47',
      buttonBg: '#8B5CF6'
    },
    layout: 'speaker', // Highlight current speaker
    minimalist: true
  },
  presentation: {
    id: 'presentation',
    name: 'Presentation',
    icon: '📊',
    colors: {
      primary: '#F59E0B',
      background: '#FFFBEB',
      darkBackground: '#1C1917',
      text: '#1C1917',
      buttonBg: '#F59E0B'
    },
    layout: 'cinema', // Small videos at top, focus on screen share
    hideControls: false
  }
};

const MeetingThemeContext = createContext();

export function MeetingThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('quickmeet_meeting_theme');
    return saved ? THEMES[saved] || THEMES.professional : THEMES.professional;
  });

  useEffect(() => {
    localStorage.setItem('quickmeet_meeting_theme', theme.id);

    // Apply theme colors to CSS variables
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-bg', theme.colors.background);
    root.style.setProperty('--theme-dark-bg', theme.colors.darkBackground);
    root.style.setProperty('--theme-text', theme.colors.text);
    root.style.setProperty('--theme-button-bg', theme.colors.buttonBg);
  }, [theme]);

  return (
    <MeetingThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </MeetingThemeContext.Provider>
  );
}

export function useMeetingTheme() {
  const context = useContext(MeetingThemeContext);
  if (!context) {
    throw new Error('useMeetingTheme must be used within MeetingThemeProvider');
  }
  return context;
}

export function ThemeSelector({ onClose }) {
  const { theme: currentTheme, setTheme, themes } = useMeetingTheme();
  const [selected, setSelected] = useState(currentTheme.id);

  const handleSelect = (themeId) => {
    setSelected(themeId);
    setTheme(themes[themeId]);
  };

  return (
    <div className="bg-[#1c1c1e] rounded-2xl p-6 shadow-2xl border border-[#2c2c2e] min-w-[400px]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Meeting Theme</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#2c2c2e] rounded transition"
          >
            <svg className="w-5 h-5 text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="space-y-2">
        {Object.values(themes).map(theme => (
          <button
            key={theme.id}
            onClick={() => handleSelect(theme.id)}
            className={`w-full p-4 rounded-xl transition-all ${
              selected === theme.id
                ? 'bg-[#06c] ring-2 ring-[#06c]/50'
                : 'bg-[#2c2c2e] hover:bg-[#3c3c3e]'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="text-3xl">{theme.icon}</div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-white">{theme.name}</div>
                <div className={`text-sm mt-1 ${selected === theme.id ? 'text-white/80' : 'text-[#8e8e93]'}`}>
                  {theme.layout === 'speaker' && 'Highlights current speaker'}
                  {theme.layout === 'cinema' && 'Perfect for presentations'}
                  {theme.layout === 'grid' && !theme.showReactions && 'Clean professional layout'}
                  {theme.showReactions && 'Fun and casual vibes'}
                  {theme.neon && 'RGB everything!'}
                </div>
              </div>
              <div className="flex gap-1">
                <div
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ backgroundColor: theme.colors.primary }}
                />
                <div
                  className="w-4 h-4 rounded-full border border-white/20"
                  style={{ backgroundColor: theme.colors.buttonBg }}
                />
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-4 p-3 bg-[#2c2c2e] rounded-lg">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-[#06c] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-xs text-[#8e8e93]">
            Themes change the look and feel of your meeting. Your preference is saved for next time!
          </div>
        </div>
      </div>
    </div>
  );
}

export default MeetingThemeProvider;
